import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import ProductRowMenu from "../../components/admin/ProductRowMenu";

const levelBadge = {
  low: "bg-sky-100 text-sky-700 border-sky-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  high: "bg-rose-100 text-rose-700 border-rose-200",
};

const statusBadge = {
  enabled: "bg-emerald-100 text-emerald-700 border-emerald-200",
  disabled: "bg-red-100 text-red-700 border-red-200",
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/products");
      setProducts(Array.isArray(res.data) ? res.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const categories = useMemo(
    () => ["all", ...new Set(products.map((p) => p.category).filter(Boolean))],
    [products]
  );

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesQuery =
        !query || product.name?.toLowerCase().includes(query.toLowerCase());
      const matchesCategory =
        selectedCategory === "all" || product.category === selectedCategory;
      const level = (product.intensity_level || "medium").toLowerCase();
      const matchesLevel = selectedLevel === "all" || level === selectedLevel;
      return matchesQuery && matchesCategory && matchesLevel;
    });
  }, [products, query, selectedCategory, selectedLevel]);

  const enabledCount = products.filter((p) => p.is_active).length;
  const highIntensityCount = products.filter(
    (p) => (p.intensity_level || "").toLowerCase() === "high"
  ).length;

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-amber-100 p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Product Catalog</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage category, intensity profile, status and edit access from one place.
            </p>
          </div>
          <button
            onClick={() => navigate("/admin/products/new")}
            className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
          >
            + Add Product
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mt-5">
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs uppercase tracking-wide text-gray-500">Total Products</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{products.length}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs uppercase tracking-wide text-gray-500">Enabled</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1">{enabledCount}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs uppercase tracking-wide text-gray-500">High Intensity</p>
            <p className="text-2xl font-bold text-rose-700 mt-1">{highIntensityCount}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-amber-100 p-4 shadow-sm">
        <div className="grid md:grid-cols-3 gap-3">
          <input
            className="input w-full"
            placeholder="Search product name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select
            className="input w-full"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category === "all" ? "All categories" : category}
              </option>
            ))}
          </select>
          <select
            className="input w-full"
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
          >
            <option value="all">All intensity levels</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-amber-100 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-amber-50 text-left text-gray-700">
              <tr>
                <th className="p-4 font-semibold">Product</th>
                <th className="font-semibold">Category</th>
                <th className="font-semibold">Intensity</th>
                <th className="font-semibold">Status</th>
                <th className="text-right p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                filteredProducts.map((p) => {
                  const level = (p.intensity_level || "medium").toLowerCase();
                  const metric = p.intensity_metric || "flavor";
                  const status = p.is_active ? "enabled" : "disabled";
                  return (
                    <tr key={p.id} className="border-t border-gray-100 hover:bg-amber-50/40">
                      <td className="p-4">
                        <p className="font-semibold text-gray-900">{p.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{p.slug}</p>
                      </td>
                      <td className="capitalize text-gray-700">{p.category}</td>
                      <td>
                        <div className="flex gap-2 items-center">
                          <span className="text-xs uppercase tracking-wide text-gray-500">
                            {metric}
                          </span>
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                              levelBadge[level] || levelBadge.medium
                            }`}
                          >
                            {level.toUpperCase()}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                            statusBadge[status]
                          }`}
                        >
                          {p.is_active ? "Enabled" : "Disabled"}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/admin/products/${p.id}/edit`)}
                            className="px-3 py-1.5 rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50 text-xs font-semibold"
                          >
                            Quick Edit
                          </button>
                          <ProductRowMenu productId={p.id} />
                        </div>
                      </td>
                    </tr>
                  );
                })}

              {!loading && filteredProducts.length === 0 && (
                <tr className="border-t border-gray-100">
                  <td className="p-6 text-sm text-gray-500" colSpan={5}>
                    No products found for selected filters.
                  </td>
                </tr>
              )}

              {loading && (
                <tr className="border-t border-gray-100">
                  <td className="p-6 text-sm text-gray-500" colSpan={5}>
                    Loading products...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;
